import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { PageDirectoryEntryName } from "../../api/PageDirectory";
import { formatDate } from "../../helpers/dates";
import { useAuth } from "../../providers/AuthProvider";
import { Dossier, DossierStatusLabelMap } from "../../types/Dossier";

interface DossierSelectorProps {
  dossiers: Dossier[];
  selectedDossier: Dossier;
  redirectTo: PageDirectoryEntryName;
  showDivider?: boolean;
}

const DossierSelector = ({
  dossiers,
  selectedDossier,
  redirectTo,
  showDivider = false,
}: DossierSelectorProps) => {
  const navigate = useNavigate();
  const { getScopedPageUrl } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const selectedRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDossierSelect = (dossierId: string) => {
    navigate(getScopedPageUrl(redirectTo, dossierId));
    handleClose();
  };

  const formatDossierName = (dossier: Dossier) => {
    return `${dossier.vehicleModelyear} ${dossier.vehicleMakeName} ${dossier.vehicleModelName}`;
  };

  const formatDossierStatus = (dossier: Dossier) => {
    const dateString =
      dossier.startDate && dossier.endDate
        ? `: ${formatDate(dossier.startDate, "DD/MM/YYYY")} - ${formatDate(
            dossier.endDate,
            "DD/MM/YYYY"
          )}`
        : "";
    return `${DossierStatusLabelMap[dossier.status]}${dateString}`;
  };

  const filteredDossiers = dossiers.filter(
    (dossier) => dossier.id !== selectedDossier.id
  );
  const disableMenu = filteredDossiers.length === 0;

  return (
    <Box mb={2}>
      <Box
        ref={selectedRef}
        onClick={disableMenu ? undefined : handleClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 16px",
          paddingLeft: "0",
          borderRadius: "4px",
          cursor: disableMenu ? "default" : "pointer",
          "&:hover": {
            backgroundColor: disableMenu ? "transparent" : "neutralGray.light",
          },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="titleMedium" color="primary.main">
            {formatDossierName(selectedDossier)}
          </Typography>
          <Typography variant="labelLarge">
            {formatDossierStatus(selectedDossier)}
          </Typography>
        </Box>
        {!disableMenu && (
          <KeyboardArrowDownIcon
            sx={{
              fontSize: "20px",
              ml: 1,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        )}
      </Box>

      {!disableMenu && (
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={{
            paper: {
              sx: {
                width: selectedRef.current?.offsetWidth,
              },
            },
          }}
        >
          {filteredDossiers.map((dossier) => (
            <MenuItem
              key={dossier.id}
              onClick={() => handleDossierSelect(dossier.id)}
              sx={{
                padding: "12px 16px",
              }}
            >
              <Box sx={{ width: "100%" }}>
                <Typography variant="titleSmall" color="primary.main">
                  {formatDossierName(dossier)}
                </Typography>
                <Typography variant="labelMedium">
                  {formatDossierStatus(dossier)}
                </Typography>
              </Box>
            </MenuItem>
          ))}
        </Menu>
      )}
      {showDivider && <Divider sx={{ borderColor: "primary.main" }} />}
    </Box>
  );
};

export default DossierSelector;
